import { useTranslation } from 'react-i18next';
import { EstateStatusItemState } from '../../generated/graphql';
import {
  ClientEstateStatusScoreHealthRange,
  StandaloneScorecardElement,
} from '../../UI';
import { ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL } from '../../Scorecard';
import { calculateEstateStatusSummary, formatEstateStatusWeight } from '../util';
import type { EstateStatusReadOnlyChecklistItem } from '../types';
import { EstateStatusChecklistItemCard } from './EstateStatusChecklistItemCard';

interface EstateStatusReadOnlySummaryProps {
  checklist: EstateStatusReadOnlyChecklistItem[];
  firmClientID?: string | null;
  firmID?: string | null;
  showScoreHealthRange?: boolean;
}

function formatLastReviewedDate(value: string | null | undefined, locale: string) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function EstateStatusReadOnlySummary({
  checklist,
  firmClientID,
  firmID,
  showScoreHealthRange = false,
}: EstateStatusReadOnlySummaryProps) {
  const { t, i18n } = useTranslation('estateStatus');
  const summary = calculateEstateStatusSummary(checklist);
  const applicableItemLabel = t(summary.applicable === 1 ? 'item-singular' : 'item-plural');
  const completedLabel =
    summary.applicable === 0
      ? t('no-applicable-items')
      : t('completed-label', {
          completed: summary.completed,
          applicable: summary.applicable,
          itemLabel: applicableItemLabel,
        });
  const estateStatusScorecardElementValue = summary.score == null ? null : summary.score / 100;
  const stateLabels: Record<EstateStatusItemState, string> = {
    [EstateStatusItemState.Yes]: t('state-yes'),
    [EstateStatusItemState.No]: t('state-no'),
    [EstateStatusItemState.Na]: t('state-na'),
  };

  return (
    <div className="pb-10">
      <div className="space-y-4">
        <div className="sm:mt-[43px] sm:flex">
          <StandaloneScorecardElement
            element={ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL}
            value={estateStatusScorecardElementValue}
          />

          <div className="mt-3 flex max-w-[422px] flex-1 flex-col rounded-[10px] bg-darkMode-gray-medium/[0.16] px-6 pb-[30px] sm:ml-[19px] sm:mt-0">
            <div className="mb-2 mt-[6px] flex h-12 items-center justify-between text-base text-darkMode-gray-medium">
              <p className="font-bold">{t('calculation-factors')}</p>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                  {t('checklist-progress')}
                </p>
                <p className="mt-2 text-2xl font-semibold text-white">{completedLabel}</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                    {t('earned-weight')}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-white">
                    {formatEstateStatusWeight(summary.earnedWeight)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                    {t('applicable-weight')}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-white">
                    {formatEstateStatusWeight(summary.applicableWeight)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                    {t('template-weight')}
                  </p>
                  <p className="mt-1 text-xl font-semibold text-white">
                    {formatEstateStatusWeight(summary.totalWeight)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showScoreHealthRange && firmClientID && firmID ? (
          <ClientEstateStatusScoreHealthRange className="mt-10" firmClientID={firmClientID} firmID={firmID} />
        ) : null}

        <div className="rounded-3xl border border-darkMode-gray-medium/30 bg-darkMode-gray-darkest/95 p-5">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-darkMode-gray-light/75">
            {t('current-score')}
          </p>
          <p className="mt-2 text-4xl font-semibold text-white">
            {summary.score == null ? t('not-applicable') : formatEstateStatusWeight(summary.score)}
          </p>
          <p className="mt-4 text-sm text-darkMode-gray-light/70">{t('client-read-only-description')}</p>
        </div>

        {checklist.map((item) => {
          const lastReviewedDate = formatLastReviewedDate(item.lastReviewedAt, i18n.language);

          return (
            <EstateStatusChecklistItemCard
              key={item.id}
              item={item}
              topContent={
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('state-label')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-white">{stateLabels[item.state]}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('last-reviewed-label')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-white">
                      {lastReviewedDate ?? t('last-reviewed-empty')}
                    </p>
                  </div>
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}
